/*!
 * This file was created to avoid "values imports" (constants/enums/classes/etc.) from the `bitmovin-player` package,
 * which would include the whole package in our distribution code.
 *
 * We should still use "type imports" (`import type {}`) from the library!
 *
 * Unit test should cover "copies of the library values" to be sure they are up to date with the library!
 */
import type {LatencyMode, PlayerEvent} from 'bitmovin-player';

/**
 * Map of Bitmovin Player events that we listen to, each should be of type {@link PlayerEvent}.
 *
 * Why: This way, we don't need to import string enum {@link PlayerEvent} from 'bitmovin-player' directly,
 * aka. compile/include it (as value) in our distribution code
 *
 * @see https://github.com/bitmovin-engineering/bitdash/blob/v8.281.0/src/core/Events.ts
 */
export const BITMOVIN8_PLAYER_EVENT = {
  AdBreakFinished: 'adbreakfinished' as PlayerEvent.AdBreakFinished,
  AdBreakStarted: 'adbreakstarted' as PlayerEvent.AdBreakStarted,
  AdClicked: 'adclicked' as PlayerEvent.AdClicked,
  AdError: 'aderror' as PlayerEvent.AdError,
  AdFinished: 'adfinished' as PlayerEvent.AdFinished,
  AdManifestLoaded: 'admanifestloaded' as PlayerEvent.AdManifestLoaded,
  AdQuartile: 'adquartile' as PlayerEvent.AdQuartile,
  AdSkipped: 'adskipped' as PlayerEvent.AdSkipped,
  AdStarted: 'adstarted' as PlayerEvent.AdStarted,
  AudioChanged: 'audiochanged' as PlayerEvent.AudioChanged,
  AudioPlaybackQualityChanged: 'audioplaybackqualitychanged' as PlayerEvent.AudioPlaybackQualityChanged,
  CastStarted: 'caststarted' as PlayerEvent.CastStarted,
  CastStopped: 'caststopped' as PlayerEvent.CastStopped,
  Destroy: 'destroy' as PlayerEvent.Destroy,
  DownloadFinished: 'downloadfinished' as PlayerEvent.DownloadFinished,
  Error: 'error' as PlayerEvent.Error,
  LatencyModeChanged: 'latencymodechanged' as PlayerEvent.LatencyModeChanged,
  LicenseValidated: 'licensevalidated' as PlayerEvent.LicenseValidated,
  Muted: 'muted' as PlayerEvent.Muted,
  Paused: 'paused' as PlayerEvent.Paused,
  Play: 'play' as PlayerEvent.Play,
  PlaybackFinished: 'playbackfinished' as PlayerEvent.PlaybackFinished,
  Playing: 'playing' as PlayerEvent.Playing,
  Seek: 'seek' as PlayerEvent.Seek,
  Seeked: 'seeked' as PlayerEvent.Seeked,
  SegmentPlayback: 'segmentplayback' as PlayerEvent.SegmentPlayback,
  SourceLoaded: 'sourceloaded' as PlayerEvent.SourceLoaded,
  SourceUnloaded: 'sourceunloaded' as PlayerEvent.SourceUnloaded,
  StallEnded: 'stallended' as PlayerEvent.StallEnded,
  StallStarted: 'stallstarted' as PlayerEvent.StallStarted,
  TimeChanged: 'timechanged' as PlayerEvent.TimeChanged,
  TimeShifted: 'timeshifted' as PlayerEvent.TimeShifted,
  SubtitleDisabled: 'subtitledisabled' as PlayerEvent.SubtitleDisabled,
  SubtitleEnabled: 'subtitleenabled' as PlayerEvent.SubtitleEnabled,
  TargetLatencyChanged: 'targetlatencychanged' as PlayerEvent.TargetLatencyChanged,
  Unmuted: 'unmuted' as PlayerEvent.Unmuted,
  VideoPlaybackQualityChanged: 'videoplaybackqualitychanged' as PlayerEvent.VideoPlaybackQualityChanged,
  ViewModeChanged: 'viewmodechanged' as PlayerEvent.ViewModeChanged,
} as const;

/**
 * Map of the modes the Bitmovin Player latency control reports, each should be of type
 * {@link LatencyMode}.
 *
 * Why: see {@link BITMOVIN8_PLAYER_EVENT}
 *
 * @see https://github.com/bitmovin-engineering/bitdash/blob/v8.281.0/src/core/Events.ts
 */
export const BITMOVIN8_LATENCY_MODE = {
  Catchup: 'catchup' as LatencyMode.Catchup,
  Fallback: 'fallback' as LatencyMode.Fallback,
  Idle: 'idle' as LatencyMode.Idle,
  Suspended: 'suspended' as LatencyMode.Suspended,
} as const;
